'use client'
import { useEffect, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import { CapacityTableColumns, DayPickerProps, Inventory, VoyageInfo } from '@/root/shared-types'
import {
  checkDangerousGoods,
  getInventoryAsArray,
  getResidentInventoryValues,
  getSoldOutText,
  hasBicycleAvailability,
  hasFutureSail,
  hasPassengerAvailabilities,
  hasResidentAvailability,
  hasSoldOut,
  isSailCancelled,
  isSailSoldOut,
  useRouteAttributeText,
} from '@/root/modules/shared/SelectDateTimePage'
import { useAppConfig, useCustomRouter, useGetTranslations, useTravelDurationMessage } from '@/root/shared-hooks'
import { hasCarAvailabilities } from '@/root/shared-helpers/sail-select/showWarningsAndColumns'
import { checkSailingAvailability } from '@/root/shared-helpers/reservation/check-sailing-availability'

type InventoryItem = ReturnType<typeof getInventoryAsArray>[number]

export interface UseRouteSectionParams extends Pick<DayPickerProps, 'selectedDate' | 'onDayClick'> {
  inventory: Inventory
  routeInfo: VoyageInfo
  sailRefId: number
  initialTimestamp?: DateTime
  // for edit section
  requiredBicycles?: number
  requiredPassengers?: number
  requiredResidentPassengers?: number
  requiredResidentVehicles?: number
  requiredVehicles?: number
  routeAttributes?: Record<string, boolean | string>
  swapPorts?: () => void
}

export const useRouteSection = ({
  inventory,
  routeInfo,
  sailRefId,
  selectedDate,
  onDayClick,
  initialTimestamp,
  routeAttributes,
  swapPorts,
  requiredPassengers,
  requiredResidentPassengers,
  requiredVehicles,
  requiredResidentVehicles,
  requiredBicycles,
}: UseRouteSectionParams) => {
  const { getTranslation } = useGetTranslations()
  const { locale } = useCustomRouter()
  const { defaultTimezone } = useAppConfig()
  const getAttributeText = useRouteAttributeText(routeAttributes)
  const durationMessage = useTravelDurationMessage(routeInfo.duration)

  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const toggleCalendar = () => setIsCalendarOpen(!isCalendarOpen)

  const handleDayClick = (date: Date) => {
    const isEqual = selectedDate?.toDateString() === date.toDateString()
    if (isEqual) {
      setIsCalendarOpen(false)
      return
    }
    onDayClick?.(date)
    setIsCalendarOpen(false)
  }

  const handleSwapPorts = () => {
    setIsCalendarOpen(false)
    swapPorts?.()
  }

  const hasUpcomingSail = hasFutureSail({ defaultTimezone, inventory, initialTime: initialTimestamp })
  const mergedData = getInventoryAsArray({ defaultTimezone, inventory, initialTime: initialTimestamp })

  const showColumns: CapacityTableColumns = {
    showPassenger: hasPassengerAvailabilities(inventory),
    showResident: hasResidentAvailability(inventory),
    showBicycle: hasBicycleAvailability(inventory),
    showCar: hasCarAvailabilities(inventory),
  }

  const showDangerGoods = checkDangerousGoods(inventory)
  const showAnchor = mergedData.some(isSailCancelled)

  const { soldOutPassenger, soldOutBicycle } = hasSoldOut(inventory)
  const soldOutResident = mergedData.some((item) => {
    const { amount } = getResidentInventoryValues(item.passengerDeck)
    return amount === 0
  })
  const showSoldOut =
    (showColumns.showPassenger && soldOutPassenger) ||
    (showColumns.showResident && soldOutResident) ||
    (showColumns.showBicycle && soldOutBicycle)

  const soldOutWarning = getSoldOutText(getAttributeText, getTranslation('outOfStockPart1'))

  const formatSelectedDate = (date?: Date) => {
    if (!date) return ''
    const dt = DateTime.fromJSDate(date)
    const weekdayFull = dt.setLocale(locale).toFormat('ccc').toUpperCase()
    const weekday = locale === 'et' ? weekdayFull.slice(0, 1) : weekdayFull.slice(0, 2)
    return `${weekday} ${dt.toFormat('dd.MM.yyyy')}`
  }

  const selectedIndex = mergedData.findIndex((item) => sailRefId === Number(item.sailRefId))
  const routeName = `${routeInfo.departurePort} - ${routeInfo.arrivalPort}`

  // Edit mode only: whether the sail fits the tickets already in the reservation
  const getAvailability = (item: InventoryItem) =>
    requiredPassengers !== undefined
      ? checkSailingAvailability(item, {
          passengers: requiredPassengers ?? 0,
          residentPassengers: requiredResidentPassengers ?? 0,
          vehicles: requiredVehicles ?? 0,
          residentVehicles: requiredResidentVehicles ?? 0,
          bicycles: requiredBicycles ?? 0,
        })
      : null

  // A sail that can't be picked: too small for the reservation in edit mode, sold out on purchase
  const isSailUnavailable = (item: InventoryItem) => {
    const availability = getAvailability(item)
    return availability ? !availability.isAvailable : isSailSoldOut(item)
  }

  const calendarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsCalendarOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return {
    durationMessage,
    isCalendarOpen,
    toggleCalendar,
    calendarRef,
    handleDayClick,
    handleSwapPorts,
    hasUpcomingSail,
    mergedData,
    showColumns,
    showDangerGoods,
    showAnchor,
    showSoldOut,
    soldOutWarning,
    selectedDateLabel: formatSelectedDate(selectedDate),
    selectedIndex,
    routeName,
    getAvailability,
    isSailUnavailable,
  }
}
